import {
  ArrowDownTrayIcon,
  ArrowsRightLeftIcon,
  ArrowUpTrayIcon,
  ArrowUturnLeftIcon,
  BanknotesIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  CreditCardIcon,
  DocumentArrowDownIcon,
  DocumentPlusIcon,
  EnvelopeIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { TargetBox } from 'components/DragDrop'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import { useSharedLoan } from 'context/SharedLoanContext'
import { calculateAggregateAdjustment } from 'pages/Loan/ClosingScreen/HUD1Page2'
import { tmoReports } from 'pages/Loan/Reports/constant'
import { PaymentImportModal } from 'pages/Servicing/PaymentImport/PaymentImportModal'
import { isPaymentHistoryFile } from 'pages/Servicing/PaymentImport/PaymentImportWizard'
import { useEffect, useMemo, useState } from 'react'
import { NativeTypes } from 'react-dnd-html5-backend'
import { toast } from 'react-toastify'
import {
  addNewLoanCharge,
  createLoanPayment,
  deleteLoanPayment,
  downloadLoanPayments,
  downloadServicingReportWithOptions,
  generateNsfNotice,
  gethud1PageData,
  getLoanPaymentFillData,
  getLoanPayments,
  getNsfConfig,
  getNsfNotice,
  getPurchaseAdviceData,
  getServicingNotes,
  sendNsfNotice,
  updateLoanPayment,
} from 'services'
import { InputDateRange, Modal, PlainTable, Tooltip } from 'stories/components'
import {
  calculateNextPaymentDate,
  confirm,
  convertNegative2Parentheses,
  formatDate,
  getPrice2decimal,
  Num,
  openAuditLog,
  removeComma,
  renderSortButton,
} from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { LoanChargeType } from '../LoanCharges'
import { AddLoanCharge } from '../LoanCharges/AddLoanCharge'
import { usePayoffGuard } from '../payoffGuard'
import { ServicingHoldBanner, useServicingHold } from '../servicingHold'
import {
  loanPaymentAuditFieldKeys,
} from './constants'
import { DeferOption } from './LoanPaymentAdjustModal'
import { LoanPaymentsModal } from './LoanPaymentsModal'
import { PaymentKindBadges } from './PaymentKindBadges'
import { bookPaymentShortfallCharges } from './shortfallCharges'

type SortType = 'date' | 'number'

interface Column {
  title: string
  key?: string
  type?: SortType
  /** Currency columns are totaled in the footer; running balances opt out. */
  noTotal?: boolean
  /** Numeric value of the cell when it isn't a plain item[key] - used for sorting and totals. */
  value?: (item: Record<string, any>) => number
  /** Explains a derived column on hover, for one whose heading cannot carry the whole rule. */
  hint?: string
}

const columns: Column[] = [
  { title: 'Due Date', key: 'paymentDueDate', type: 'date' },
  { title: 'Date Received', key: 'paymentReceivedDate', type: 'date' },
  { title: 'Date Deposited', key: 'paymentDepositedDate', type: 'date' },
  // Measured server-side from this row's own two dates, the same rule as the pipeline's Days Late
  // column. Grace is not applied: a payment three days after its due date reads 3 here even where
  // the Late Fees cell beside it is empty, because grace decides the FEE, not the lateness. Not
  // totaled: a sum of days late says nothing.
  {
    title: 'Days Late',
    key: 'daysLate',
    type: 'number',
    noTotal: true,
    hint: "Calendar days from the due date to the date the payment was received. Grace is not deducted, so a payment inside the loan's grace period still shows the days - it just carries no late fee. Blank when the payment arrived on or before its due date.",
  },
  // How the money arrived. Sits with the descriptive columns rather than inside the
  // money block, so the numeric run stays unbroken.
  { title: 'Method', key: 'paymentMethod' },
  { title: 'Type / Notes' },
  { title: 'Amount Due', key: 'amountDue', type: 'number' },
  { title: 'Amount Paid', key: 'amountPaid', type: 'number' },
  { title: 'Principal', key: 'principal', type: 'number' },
  { title: 'Interest', key: 'interest', type: 'number' },
  { title: 'Default Interest', key: 'defaultInterest', type: 'number' },
  { title: 'Unpaid Interest', key: 'unpaidInterest', type: 'number' },
  { title: 'Unpaid Default Int', key: 'unpaidDefaultInterest', type: 'number' },
  { title: 'Capitalized Interest', key: 'capitalizedInterest', type: 'number' },
  { title: 'Mtg.Ins', key: 'mortgage', type: 'number' },
  { title: 'To Impound', key: 'escrows', type: 'number' },
  { title: 'Buydowns', key: 'buydown', type: 'number' },
  { title: 'Late Fees', key: 'lateFee', type: 'number' },
  { title: 'Charges', key: 'charges', type: 'number' },
  { title: 'Servicing Fees', key: 'servicingFee', type: 'number' },
  { title: 'Other Payments', key: 'otherPaymentsTotal', type: 'number' },
  {
    title: 'Suspense',
    key: 'sourceSuspense',
    type: 'number',
    value: (item) => Num(item.suspense) - Num(item.sourceSuspense),
  },
  { title: 'From Impound', key: 'sourceImpound', type: 'number' },
  { title: 'Interest Reserve', key: 'sourceInterestReserve', type: 'number' },
  // The other two buckets a payment can be funded from. Listed beside the Interest Reserve one
  // because they are the same kind of money and belong in the same part of the row: without them
  // a payoff settled out of a construction reserve or a held trust balance showed its Total
  // Credited with no column accounting for where the funds came from. Blank on a loan that has
  // never drawn one, exactly as the three above already are.
  { title: 'Construction Reserve', key: 'sourceConstructionReserve', type: 'number' },
  { title: 'Escrow Holdback', key: 'sourceEscrowRepairHoldback', type: 'number' },
  { title: 'Trust Balance', key: 'sourceTrustBalance', type: 'number' },
  { title: 'Total Credited', key: 'totalCredited', type: 'number' },
  { title: 'Unpaid Balance', key: 'unpaidBalance', type: 'number', noTotal: true },
  { title: 'Action' },
]

// Currency cell for the payments grid: right-aligned "$1,234.56" (2 decimals, thousands
// separators, $ prefix), blank for empty/null so unused columns stay clean.
const money = (v: any) =>
  v === null || v === undefined || v === '' ? (
    ''
  ) : (
    <div className="text-right tabular-nums">{getPrice2decimal(v, false, true, true)}</div>
  )

/** Numeric value behind a currency cell, so the grid and its totals never drift apart. */
const columnValue = (item: Record<string, any>, key: string) => {
  const column = columns.find((c) => c.key === key)
  return column?.value ? column.value(item) : Num(item[key])
}

const providerLabels: Record<string, string> = {
  usio: 'USIO',
  stripe: 'Stripe',
}

const providerMethodLabels: Record<string, string> = {
  card: 'Card',
  us_bank_account: 'ACH',
  pinless_debit: 'Debit',
}

/**
 * Chip marking a payment that was funded by a gateway charge rather than a check or a manual entry,
 * with the provider's reference on hover so it can be traced back to their transaction.
 */
const ProviderBadge = ({ payment: providerRef }: { payment: Record<string, any> }) => {
  const provider = providerLabels[providerRef.provider] || providerRef.provider
  const method = providerMethodLabels[providerRef.method] || ''
  const taken = providerRef.source === 'phone' ? 'Paid by phone' : 'Paid online'
  const lines = [
    `${taken} via ${provider}`,
    `Reference: ${providerRef.confirmation || providerRef.intentId}`,
    method ? `Method: ${method}${providerRef.last4 ? ` ****${providerRef.last4}` : ''}` : '',
    providerRef.convenienceFee ? `Convenience fee: $${getPrice2decimal(providerRef.convenienceFee, false, true)}` : '',
    providerRef.chargedBy ? `Taken by: ${providerRef.chargedBy}` : '',
  ].filter(Boolean)
  return (
    <Tooltip message={lines.join('\n')}>
      <span className="inline-flex items-center gap-1 rounded-full bg-shade-blue/10 px-2 py-[1px] text-[11px] font-medium text-shade-blue whitespace-nowrap">
        <CreditCardIcon className="h-3 w-3" />
        {provider}
        {providerRef.last4 ? ` ****${providerRef.last4}` : ''}
      </span>
    </Tooltip>
  )
}

/**
 * Chip carrying the reference an ACH draft can be matched back by.
 *
 * "ACHPmt" alone says a draft run applied the payment but gives a servicer nothing to reconcile
 * against: a returned draft comes back from the bank quoting the trace number the file went out
 * under, which is the same field a prior servicer's system shows as the transaction's Reference.
 * The trace leads for that reason, with the run's own reference behind it on hover for a run that
 * was submitted to a provider instead of taken to the bank as a file.
 */
const AchReferenceBadge = ({ reference }: { reference: Record<string, any> }) => {
  const shown = reference.trace || reference.reference || (reference.runId ? `Run #${reference.runId}` : '')
  if (!shown) return null
  const lines = [
    reference.trace ? `Trace: ${reference.trace}` : '',
    reference.reference ? `Run reference: ${reference.reference}` : '',
    reference.runId ? `ACH run #${reference.runId}` : '',
  ].filter(Boolean)
  return (
    <Tooltip message={lines.join('\n')}>
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-[1px] text-[11px] font-medium text-slate-600 whitespace-nowrap">
        <BanknotesIcon className="h-3 w-3" />
        {shown}
      </span>
    </Tooltip>
  )
}

/** The reverse reason that triggers NSF handling (fee + notice). Must match the option value. */
const NSF_REASON = 'Non sufficient funds'
/** Matches the filename the backend gives a generated notice. */
const NSF_NOTICE_PREFIX = 'NSF Notice'

export default function LoanPayments() {
  // Warns before entering a payment against an active payoff (same guard as the Trust Ledger).
  const confirmPayoff = usePayoffGuard()
  // Hold is enforced when the payment is saved. Say so on the way in, rather than after the user
  // has filled the whole form.
  const onHold = useServicingHold()
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  // The importer posts to an explicit loan (it is also used portfolio-wide), so it needs the number.
  const loanNumber = Number(isShared ? shared?.loanNumber : setLoanNumber()) || 0

  /**
   * Hold blocks applying and editing payments, and the save enforces it whatever the screen does.
   * Refusing here is what stops a servicer filling in a whole payment first - and it has to sit on
   * the handler rather than the Add link, because the pipeline's "Apply Payment" action deep-links
   * straight past the link with ?addPayment=1.
   */
  const blockedByHold = () => {
    if (!onHold) return false
    toast(
      `Loan ${loanNumber} is on Hold, so payments cannot be applied or edited. ` +
        `Remove the Hold on the Servicing Information screen to continue.`,
      { type: 'warning' },
    )
    return true
  }
  const [isLoading, setIsLoading] = useState(false)
  const [isModalLoading, setIsModalLoading] = useState(false)
  const [data, setData] = useState<Array<Record<string, any>>>([])
  const [showAdd, setShowAdd] = useState(false)
  // Summary tables (Amount Due / Balances) sit at the bottom of the page, open by default:
  // servicing reads them on nearly every visit, and reopening them each time was busywork.
  const [showAmountDue, setShowAmountDue] = useState(true)
  const [showBalances, setShowBalances] = useState(true)
  const [dateFilters, setDateFilters] = useState<{
    dueDate: { from?: string; to?: string }
    receivedDate: { from?: string; to?: string }
    depositedDate: { from?: string; to?: string }
  }>({
    dueDate: {},
    receivedDate: {},
    depositedDate: {},
  })
  const [editData, setEditData] = useState<Record<string, any>>({})
  const [paymentInfo, setPaymentInfo] = useState<Record<string, any>>({})
  const [purchaseAdviceInfo, setPurchaseAdviceInfo] = useState<Record<string, any>>({})
  const [hud1Info, setHud1Info] = useState<Record<string, any>>({})
  const [servicingNextPaymentDate, setServicingNextPaymentDate] = useState<string>('')
  const [isDownloading, setIsDownloading] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [showReverseModal, setShowReverseModal] = useState(false)
  const [showAddCharge, setShowAddCharge] = useState(false)
  const [reversePayment, setReversePayment] = useState<Record<string, any> | null>(null)
  const [reverseReasonInput, setReverseReasonInput] = useState<InputType>({
    inputType: 'select',
    title: 'Select Reason',
    value: '',
    options: {
      [NSF_REASON]: NSF_REASON,
      'Incorrect Payment amount': 'Incorrect Payment amount',
      'Bank account closed/frozen': 'Bank account closed/frozen',
      Other: 'Other',
    },
    hasDefaultOption: true,
    defaultOptionText: '-- Select Reason --',
    span: 1,
  })
  const [otherReasonInput, setOtherReasonInput] = useState<InputType>({
    inputType: 'textarea',
    title: 'Please specify the reason',
    value: '',
    span: 1,
    required: true,
  })
  // NSF extras. Only surfaced when the reason is NSF: the fee is prompted (pre-filled from the
  // ACH config default) and the notice is generated for review, emailed right away only if asked.
  const [nsfConfig, setNsfConfig] = useState<{ fee: number; template: string; autoEmail: boolean }>({
    fee: 0,
    template: '',
    autoEmail: false,
  })
  const [nsfFee, setNsfFee] = useState<string>('')
  const [nsfGenerateNotice, setNsfGenerateNotice] = useState(true)
  // Defaults to the ACH config setting; the servicer can still flip it for this one event.
  const [nsfEmailNotice, setNsfEmailNotice] = useState(false)
  const [sendingNoticeId, setSendingNoticeId] = useState<number | null>(null)
  const [generatingNoticeId, setGeneratingNoticeId] = useState<number | null>(null)
  const [distributionReportId, setDistributionReportId] = useState<number | null>(null)
  // Oldest to newest by due date, which is how servicing reads a payment history.
  const [sortConfig, setSortConfig] = useState<{ key: string; order: number }>({
    key: 'paymentDueDate',
    order: 1,
  })

  const initData = async () => {
    setIsLoading(true)
    const [loanPayments, paymentData, purchaseAdviceData, hud1Data, servicingNotes] = await Promise.all([
      getLoanPayments(),
      getLoanPaymentFillData(),
      getPurchaseAdviceData(),
      gethud1PageData('page2'),
      getServicingNotes().catch(() => null),
    ])
    // NSF defaults (fee + whether a notice template is configured). Non-fatal: without it the
    // fee simply isn't pre-filled and the notice option stays hidden.
    getNsfConfig()
      .then((res) => {
        setNsfConfig({
          fee: Number(res?.fee) || 0,
          template: res?.template || '',
          autoEmail: res?.autoEmail ?? false,
        })
        setNsfEmailNotice(res?.autoEmail ?? false)
      })
      .catch(() => null)
    // Amount Due, monthly Escrow and interest Rate are driven by the Servicing
    // Information screen (Total Payment / Escrow Monthly / Current Interest Rate);
    // fall back to the fill-data values when they aren't available.
    const servicingTotalPayment = Number(removeComma(String(servicingNotes?.data?.totalPayment ?? ''))) || 0
    const servicingEscrowMonthly = Number(removeComma(String(servicingNotes?.data?.escrowMonthly ?? ''))) || 0
    const servicingCurrentRate = Number(removeComma(String(servicingNotes?.data?.currentInterestRate ?? ''))) || 0
    // Servicing Information > Interest Calculations settings drive the interest math:
    // Interest Type (Dutch/Non-Dutch method), Daily Rate Basis (day-count denominator),
    // and Accrual Period (which dates drive the day-count numerator).
    const servicingInterestType = servicingNotes?.data?.interestType
    const servicingDailyRateBasis = servicingNotes?.data?.dailyRateBasis
    const servicingAccrualPeriod = servicingNotes?.data?.accrualPeriod
    // Closing / first payment dates anchor the accrual day-count for the first payment
    // (no prior payment to measure from).
    const servicingClosingDate = servicingNotes?.data?.closingDate
    const servicingFirstPaymentDate = servicingNotes?.data?.firstPaymentDate
    // Whether uncollected interest is capitalized into Principal (neg-am) or tracked
    // as Unpaid Interest.
    const servicingUnpaidInterestAppliesTo = servicingNotes?.data?.unpaidInterestAppliesTo
    // Balances used to build the Payoff amount.
    const sd = servicingNotes?.data || {}
    setData(loanPayments)
    setPaymentInfo({
      ...paymentData,
      ...(servicingTotalPayment ? { amountDue: servicingTotalPayment } : {}),
      ...(servicingEscrowMonthly ? { escrowAmount: servicingEscrowMonthly } : {}),
      ...(servicingCurrentRate ? { rate: servicingCurrentRate } : {}),
      ...(servicingInterestType ? { isDutch: servicingInterestType === 'Dutch' } : {}),
      ...(servicingDailyRateBasis ? { dailyRateBasis: servicingDailyRateBasis } : {}),
      ...(servicingAccrualPeriod ? { accrualPeriod: servicingAccrualPeriod } : {}),
      ...(servicingClosingDate ? { closingDate: servicingClosingDate } : {}),
      ...(servicingFirstPaymentDate ? { firstPaymentDate: servicingFirstPaymentDate } : {}),
      ...(servicingUnpaidInterestAppliesTo ? { unpaidInterestAppliesTo: servicingUnpaidInterestAppliesTo } : {}),
      // Preserve "not set" (null) vs. an explicit 0 (e.g. a paid-off or reset loan) so the balance
      // math can trust a 0 UPB instead of falling back to the original loan amount.
      currentUpb: sd.currentUpb == null || sd.currentUpb === '' ? null : Number(removeComma(String(sd.currentUpb))),
      interestPaidThruDate: sd.interestPaidThruDate || '',
      unpaidInterestBalance: Number(removeComma(String(sd.unpaidInterestBalance ?? ''))) || 0,
      unpaidDefaultInterestBalance: Number(removeComma(String(sd.unpaidDefaultInterestBalance ?? ''))) || 0,
      unpaidChargesBalance: Number(removeComma(String(sd.unpaidChargesBalance ?? ''))) || 0,
      unpaidLateChargesBalance: Number(removeComma(String(sd.unpaidLateChargesBalance ?? ''))) || 0,
      escrowBalance: Number(removeComma(String(sd.escrowBalance ?? ''))) || 0,
      suspenseBalance: Number(removeComma(String(sd.suspenseBalance ?? ''))) || 0,
    })
    setServicingNextPaymentDate(servicingNotes?.data?.nextPaymentDate || '')
    setPurchaseAdviceInfo(purchaseAdviceData.data.data)
    setHud1Info(hud1Data.data)
    setIsLoading(false)
  }
  useEffect(() => {
    if (isShared) {
      const payments = shared.pageData
      setData(Array.isArray(payments) ? payments : [])
      setIsLoading(false)
      return
    }
    initData()
  }, [isShared, shared?.pageKey, shared?.pageData])

  // Auto-open the Add Payment modal when deep-linked with ?addPayment=1
  // (used by the servicing pipeline grid's "Apply Payment" action).
  useEffect(() => {
    if (isShared || isLoading) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('addPayment') === '1') {
      onAdd()
      // Strip the flag so it doesn't re-open on subsequent renders/navigation.
      params.delete('addPayment')
      const search = params.toString()
      window.history.replaceState(null, '', `${window.location.pathname}${search ? `?${search}` : ''}`)
    }
  }, [isLoading])

  const paymentIdsWithExistingReversal = useMemo(() => {
    const ids = new Set<number>()
    data.forEach((p: Record<string, any>) => {
      if (p.reversed && p.reversesPaymentId != null) {
        ids.add(Number(p.reversesPaymentId))
      }
    })
    return ids
  }, [data])

  const filteredData = useMemo(() => {
    const { dueDate, receivedDate, depositedDate } = dateFilters
    return data.filter((item: Record<string, any>) => {
      if (dueDate.from && item.paymentDueDate < dueDate.from) return false
      if (dueDate.to && item.paymentDueDate > dueDate.to) return false
      if (receivedDate.from && item.paymentReceivedDate < receivedDate.from) return false
      if (receivedDate.to && item.paymentReceivedDate > receivedDate.to) return false
      if (depositedDate.from && item.paymentDepositedDate < depositedDate.from) return false
      if (depositedDate.to && item.paymentDepositedDate > depositedDate.to) return false
      return true
    })
  }, [data, dateFilters])

  const sortedData = useMemo(() => {
    // Anchor the running Unpaid Balance to the servicing Current UPB so the two
    // screens agree: the most-recent payment's balance equals Current UPB, and
    // earlier rows add back the principal of the payments that came after them.
    // (Fall back to the amortization loan amount if Current UPB isn't available.)
    const hasUpb = paymentInfo.currentUpb != null && paymentInfo.currentUpb !== ''
    const currentUpb = Number(paymentInfo.currentUpb) || 0
    const totalPrincipal = filteredData.reduce(
      (sum: number, it: Record<string, any>) => sum + Number(it.principal || 0),
      0,
    )
    const baseLoanAmount = hasUpb ? currentUpb + totalPrincipal : Number(paymentInfo.loanAmount) || 0
    // The running balance below is a walk through the payment history, so it has to run in real
    // date order. The API returns rows by insert id, which is the order payments were keyed, and
    // on a TMO-imported loan that bears no relation to when they came due, so the walk was
    // anchoring Current UPB to an arbitrary row. Undated rows sort first, leaving the anchor on
    // the latest dated payment.
    const chronological = [...filteredData].sort((a: Record<string, any>, b: Record<string, any>) => {
      const da = String(a.paymentDueDate || '')
      const db = String(b.paymentDueDate || '')
      if (da !== db) return da < db ? -1 : 1
      const ra = String(a.paymentReceivedDate || '')
      const rb = String(b.paymentReceivedDate || '')
      if (ra !== rb) return ra < rb ? -1 : 1
      return (Number(a.id) || 0) - (Number(b.id) || 0)
    })
    let paidPrincipal = 0
    const augmented: Array<Record<string, any>> = chronological.map((item: Record<string, any>) => {
      paidPrincipal += Number(item.principal || 0)
      const unpaidBalance = Number((baseLoanAmount - paidPrincipal).toFixed(2))
      // Money collected on the payment and passed through to a third party. Stored as the payee
      // lines it queues, so the grid totals them into a column of their own.
      const otherPaymentsTotal =
        Math.round(
          ((item.otherPayments || []) as Array<Record<string, any>>).reduce(
            (sum, line) => sum + (Number(line?.amount) || 0),
            0,
          ) * 100,
        ) / 100
      // Every bucket the payment applied money to, so the row accounts for the whole of Amount
      // Paid: leaving one out reads as money received and never credited anywhere.
      const totalCredited =
        Math.round(
          (+item.principal +
            +item.interest +
            (+item.defaultInterest || 0) +
            +item.escrowsTotal +
            +item.buydown +
            +item.lateFee +
            (+item.charges || 0) +
            (+item.servicingFee || 0) +
            otherPaymentsTotal) *
            100,
        ) / 100
      return { ...item, unpaidBalance, otherPaymentsTotal, totalCredited }
    })

    if (!sortConfig.key || !sortConfig.order) return augmented

    const { key, order } = sortConfig
    const column = columns.find((c) => c.key === key)

    return [...augmented].sort((a, b) => {
      const va = a[key]
      const vb = b[key]
      const aEmpty = va === undefined || va === null || va === ''
      const bEmpty = vb === undefined || vb === null || vb === ''
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1

      if (column?.type === 'number') {
        const numA = column.value ? column.value(a) : Number(removeComma(String(va)))
        const numB = column.value ? column.value(b) : Number(removeComma(String(vb)))
        return (numA - numB) * order
      }
      return va < vb ? -order : va > vb ? order : 0
    })
  }, [filteredData, paymentInfo, sortConfig])

  const onEdit = (item: Record<string, any>) => {
    if (blockedByHold()) return
    setEditData(item)
    setShowAdd(true)
  }

  const showRowHistory = (id: number) => {
    const fields = [...loanPaymentAuditFieldKeys]
    openAuditLog({
      table: 'LoanPayment',
      field: fields.join(', '),
      fieldLabel: 'Loan Payment',
      keys: {
        id,
        field: fields,
      },
      // Avoid JSON.stringify wrapping ("Principal: 1001") so row history stays readable.
      renderValue: (value: any) => {
        if (value === null || value === undefined) return ''
        if (typeof value === 'object') return JSON.stringify(value)
        return String(value)
      },
    })
  }

  const tableData = useMemo(() => {
    return sortedData.map((item: Record<string, any>) => {
      // A reversal only carries a notice if one was generated - drives both the view and send actions.
      const hasNsfNotice =
        !!item.reversed && (item.files || []).some((f: any) => f?.name?.startsWith(NSF_NOTICE_PREFIX))
      // Whether the payment was returned is a property of the row, not of whether a letter happens
      // to exist for it. Reversals booked before the flag existed recorded the reason in the notes
      // instead, so read those too rather than leaving that history unmarked.
      const isNsf = !!item.nsf || (!!item.reversed && String(item.notes || '').trim() === NSF_REASON)
      return [
        formatDate(item.paymentDueDate),
        formatDate(item.paymentReceivedDate),
        formatDate(item.paymentDepositedDate),
        item.daysLate == null ? '' : <div className="text-right tabular-nums">{item.daysLate}</div>,
        <span className="whitespace-nowrap">{item.paymentMethod || ''}</span>,
        <div className="flex items-center gap-1.5">
          {/* The derived transaction kind (NSF included) leads, then the gateway chip that carries
              the provider reference, then whatever the servicer typed. All three share one line so
              a row is never taller than one line; a long note clips, with the whole of it on hover. */}
          <PaymentKindBadges kind={item.paymentKind} />
          {item.providerPayment && <ProviderBadge payment={item.providerPayment} />}
          {item.achReference && <AchReferenceBadge reference={item.achReference} />}
          {item.notes ? (
            <span className="truncate max-w-[14rem]" title={item.notes}>
              {item.notes}
            </span>
          ) : null}
        </div>,
        money(item.amountDue),
        money(item.amountPaid),
        money(item.principal),
        money(item.interest),
        money(item.defaultInterest),
        money(item.unpaidInterest),
        money(item.unpaidDefaultInterest),
        money(item.capitalizedInterest),
        money(item.mortgage),
        money(item.escrows),
        money(item.buydown),
        money(item.lateFee),
        money(item.charges),
        money(item.servicingFee),
        money(item.otherPaymentsTotal || ''),
        money(columnValue(item, 'sourceSuspense')),
        money(item.sourceImpound),
        money(item.sourceInterestReserve),
        money(item.sourceConstructionReserve),
        money(item.sourceEscrowRepairHoldback),
        money(item.sourceTrustBalance),
        money(item.totalCredited),
        money(item.unpaidBalance),
        isShared ? (
          <span className="text-gray-400">-</span>
        ) : (
          <div className="flex items-center gap-1">
            <span className="text-shade-blue cursor-pointer" onClick={() => onEdit(item)}>
              <PencilSquareIcon className="w-4 h-4" />
            </span>
            <Tooltip message="Audit Log">
              <span className="text-gray-500 cursor-pointer" onClick={() => showRowHistory(item.id)}>
                <ClockIcon className="w-4 h-4" />
              </span>
            </Tooltip>
            <Tooltip message="Distribution audit report for this payment">
              <span
                className={`cursor-pointer ${distributionReportId === item.id ? 'text-gray-400' : 'text-shade-blue'}`}
                onClick={() => onDistributionReport(item)}
              >
                <ArrowsRightLeftIcon className="w-4 h-4" />
              </span>
            </Tooltip>
            {!item.reversed && !paymentIdsWithExistingReversal.has(item.id) && (
              <span className="text-orange-600 cursor-pointer" onClick={() => onReverse(item)}>
                <ArrowUturnLeftIcon className="w-4 h-4" />
              </span>
            )}
            {isNsf && !hasNsfNotice && !!nsfConfig.template && (
              <Tooltip message="Generate the NSF notice">
                <span
                  className={`cursor-pointer ${generatingNoticeId === item.id ? 'text-gray-400' : 'text-shade-blue'}`}
                  onClick={() => onGenerateNsfNotice(item)}
                >
                  <DocumentPlusIcon className="w-4 h-4" />
                </span>
              </Tooltip>
            )}
            {hasNsfNotice && (
              <Tooltip message="View / download the NSF notice">
                <span className="text-shade-blue cursor-pointer" onClick={() => onViewNsfNotice(item)}>
                  <DocumentArrowDownIcon className="w-4 h-4" />
                </span>
              </Tooltip>
            )}
            {hasNsfNotice && (
              <Tooltip message="Email the NSF notice to the borrower">
                <span
                  className={`cursor-pointer ${sendingNoticeId === item.id ? 'text-gray-400' : 'text-shade-blue'}`}
                  onClick={() => onSendNsfNotice(item)}
                >
                  <EnvelopeIcon className="w-4 h-4" />
                </span>
              </Tooltip>
            )}
            <span className="text-red-600 cursor-pointer" onClick={() => onDelete(item.id)}>
              <TrashIcon className="w-4 h-4" />
            </span>
          </div>
        ),
      ]
    })
  }, [
    sortedData,
    paymentIdsWithExistingReversal,
    sendingNoticeId,
    generatingNoticeId,
    distributionReportId,
    nsfConfig.template,
    isShared,
  ])

  // Totals for every currency column, over all rows the filters leave in view - not just the
  // page on screen, so the figure means the same thing however the grid is paged or sorted.
  const tableFooter = useMemo(() => {
    if (!sortedData.length) return undefined
    return columns.map((col, index) => {
      if (index === 0)
        return (
          <div className="whitespace-nowrap">
            Totals
            <span className="ml-1 font-normal text-[11px] text-gray-500">
              ({sortedData.length} payment{sortedData.length === 1 ? '' : 's'})
            </span>
          </div>
        )
      if (col.type !== 'number' || col.noTotal || !col.key) return ''
      const sum = sortedData.reduce((acc: number, item) => acc + columnValue(item, col.key as string), 0)
      return money(Number(sum.toFixed(2)))
    })
  }, [sortedData])

  const onSort = (col: Column) => {
    if (!col.key) return
    if (sortConfig.key !== col.key) {
      setSortConfig({ key: col.key, order: col.type === 'date' ? 1 : -1 })
    } else {
      setSortConfig({ key: col.key, order: -sortConfig.order })
    }
  }

  const tableHeader = useMemo(
    () =>
      columns.map((col) => {
        if (!col.key) return col.title
        const isActive = sortConfig.key === col.key
        return renderSortButton({
          title: col.hint ? <Tooltip message={col.hint}>{col.title}</Tooltip> : col.title,
          sortOrder: isActive ? sortConfig.order : 0,
          onClick: () => onSort(col),
          btnClassName: 'items-center whitespace-nowrap',
        })
      }),
    [sortConfig],
  )

  const onAdd = async () => {
    if (blockedByHold()) return
    if (!(await confirmPayoff())) return
    setEditData({})
    setShowAdd(true)
  }

  const nextPaymentDate = useMemo(() => {
    // Next Payment Date comes from Servicing Information; it is advanced server-side
    // when a payment is applied. Fall back to payment history only when it's unset.
    if (servicingNextPaymentDate) return servicingNextPaymentDate
    const { firstPaymentDate, paymentFrequency } = paymentInfo
    let paymentDueDate
    if (data.length) {
      if (
        data[data.length - 1].reversed ||
        (data.length > 1 &&
          data[data.length - 1].paymentDueDate === data[data.length - 2].paymentDueDate &&
          Num(data[data.length - 1].amountDue) + Num(data[data.length - 2].amountDue) === 0 &&
          (data.length < 3 || Num(data[data.length - 2].amountDue) + Num(data[data.length - 3].amountDue) !== 0))
      ) {
        let cnt = 0
        for (let i = data.length - 1; i >= 0; i--) {
          if (!data[i].reversed) break
          cnt++
        }
        const unreversedData = data.filter((item) => !item.reversed)
        paymentDueDate = unreversedData[unreversedData.length - cnt].paymentDueDate
      } else
        paymentDueDate = data[data.length - 1].paymentDueDate
          ? calculateNextPaymentDate(data[data.length - 1].paymentDueDate, paymentFrequency)
          : ''
    }

    if (firstPaymentDate && (!paymentDueDate || firstPaymentDate > paymentDueDate)) {
      paymentDueDate = firstPaymentDate
    }

    return paymentDueDate
  }, [data, paymentInfo, servicingNextPaymentDate])

  const onSubmit = async (
    id: number | null,
    json: Record<string, any>,
    differedValues: Record<string, { amount: number; deferOption: DeferOption; reason?: string }>,
    waivers?: Record<string, { reason?: string }>,
  ) => {
    let res
    setIsModalLoading(true)
    try {
      if (id) res = await updateLoanPayment(id, json)
      else res = await createLoanPayment(json)
    } catch {
      // The error is already surfaced by the global API error toast (e.g. the loan is on
      // Hold). Reset the modal's loading state and keep it open so the user can adjust and retry.
      setIsModalLoading(false)
      return
    }
    // The operator was warned the loan is closed and said no. Nothing posted, and the modal stays
    // open with what they keyed, rather than closing as a save would.
    if (res?.cancelled) {
      setIsModalLoading(false)
      return
    }
    if (res.success) {
      const temp = cloneDeep(data)
      if (id) {
        temp.map((item) => {
          if (item.id == id) {
            Object.keys(json).map((key) => {
              item[key] = json[key]
            })
          }
        })
      } else {
        temp.push(res.item)
      }
      setData(temp)
      // Applying or reversing moves both servicing dates on the server - re-read them, along with
      // the balances they travel with, rather than showing the pre-change values.
      await refreshServicingDates()
      toast(`Successfully ${id ? 'updated' : 'created'} loan payment`, { type: 'success' })
    }

    // Link any charges this save creates to the payment so deleting the payment
    // removes them (undoing their effect on the servicing balances).
    await bookPaymentShortfallCharges({
      json,
      loanPaymentId: id ?? res?.item?.id,
      isNewPayment: !id,
      capitalizesInterest: paymentInfo?.unpaidInterestAppliesTo === 'principal',
      deferredValues: differedValues,
      waivers,
    })
    setEditData({})
    setIsModalLoading(false)
    setShowAdd(false)
  }

  /**
   * Re-read the servicing dates and balances after anything that changes payment history.
   * The server recomputes Next Payment Date and Interest Paid Thru from the full history, so the
   * screen has to read them back rather than guess - applying, reversing and deleting all move them.
   */
  const refreshServicingDates = async () => {
    const [notes, fill]: any[] = await Promise.all([
      getServicingNotes().catch(() => null),
      getLoanPaymentFillData().catch(() => null),
    ])
    const sd = notes?.data
    if (!sd) return
    setServicingNextPaymentDate(sd.nextPaymentDate || '')
    setPaymentInfo((prev: Record<string, any>) => ({
      ...prev,
      // Paying down an unpaid balance the loan accrues interest on shrinks the interest base.
      ...(fill && fill.interestBearingBalance != null ? { interestBearingBalance: fill.interestBearingBalance } : {}),
      // A payment that drew on the interest reserve has just spent some of it, so the panel has to
      // re-read both reserves rather than keep showing the balances from before the payment.
      ...(fill ? { interestReserveBalance: fill.interestReserveBalance ?? null } : {}),
      ...(fill ? { constructionReserveBalance: fill.constructionReserveBalance ?? null } : {}),
      // Its own field now rather than pooled into trustBalance. The Source of Funds section still
      // settles it through Trust Balance, so `trustCredit` in the modal adds the two back
      // together - see the note there before changing either one.
      ...(fill ? { escrowRepairHoldbackBalance: fill.escrowRepairHoldbackBalance ?? null } : {}),
      ...(fill ? { trustBalance: fill.trustBalance ?? null } : {}),
      // Settling a charge row changes whether the loan still itemises its unpaid interest and late
      // charges, which is what decides how much of either a payoff collects as cash. Re-read with
      // the balances below rather than left at the figures from before the payment.
      ...(fill ? { payoffUnpaidInterestDue: fill.payoffUnpaidInterestDue ?? 0 } : {}),
      ...(fill ? { payoffUnpaidLateChargesDue: fill.payoffUnpaidLateChargesDue ?? 0 } : {}),
      interestPaidThruDate: sd.interestPaidThruDate || '',
      unpaidInterestBalance: Number(removeComma(String(sd.unpaidInterestBalance ?? ''))) || 0,
      unpaidDefaultInterestBalance: Number(removeComma(String(sd.unpaidDefaultInterestBalance ?? ''))) || 0,
      unpaidChargesBalance: Number(removeComma(String(sd.unpaidChargesBalance ?? ''))) || 0,
      unpaidLateChargesBalance: Number(removeComma(String(sd.unpaidLateChargesBalance ?? ''))) || 0,
      escrowBalance: Number(removeComma(String(sd.escrowBalance ?? ''))) || 0,
      suspenseBalance: Number(removeComma(String(sd.suspenseBalance ?? ''))) || 0,
      currentUpb: sd.currentUpb == null || sd.currentUpb === '' ? null : Number(removeComma(String(sd.currentUpb))),
    }))
  }

  const onDelete = async (id: number) => {
    const res = await confirm('Are you sure want to remove this loan payment?')
    if (!res) return
    await deleteLoanPayment(id)
    const temp: Array<Record<string, any>> = []
    data.map((item: Record<string, any>) => {
      if (item.id == id) return
      temp.push(item)
    })
    setData(temp)
    // Deleting rolls the servicing dates back on the server - show the new ones.
    await refreshServicingDates()
    toast('Successfully removed loan payment', { type: 'success' })
  }

  const onReverse = async (item: Record<string, any>) => {
    const res = await confirm('Are you sure you want to reverse this transaction?')
    if (!res) return
    setReversePayment(item)
    const temp = cloneDeep(reverseReasonInput)
    temp.value = ''
    setReverseReasonInput(temp)
    setNsfFee(nsfConfig.fee ? String(nsfConfig.fee) : '')
    setNsfGenerateNotice(true)
    setShowReverseModal(true)
  }

  // Open the generated notice in a new tab so it can be reviewed (and printed/saved) before sending.
  const onViewNsfNotice = async (item: Record<string, any>) => {
    try {
      const blob: Blob = await getNsfNotice(item.id)
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch {
      toast('Could not open the NSF notice.', { type: 'error' })
    }
  }

  /**
   * The BORROWER PAYMENT DISTRIBUTION AUDIT REPORT for this one payment: what came in and which
   * payees it funded, closing with the difference that should be zero. Scoped by `paymentId`, which
   * the report honours ahead of any date window, so the row clicked is the row answered for.
   *
   * `loanNumbers` is passed explicitly because that is the scope the server treats as final: it
   * skips the servicing-status gate, so the report still runs on a paid-off or transferred loan,
   * which is exactly when somebody is auditing where a payment went.
   */
  const onDistributionReport = async (item: Record<string, any>) => {
    if (distributionReportId) return
    setDistributionReportId(item.id)
    try {
      await downloadServicingReportWithOptions({
        type: tmoReports.borrowerPaymentDistributionAudit,
        loanNumbers: [loanNumber],
        paymentId: item.id,
        format: 'pdf',
      })
    } catch {
      toast('Could not build the distribution report for this payment.', { type: 'error' })
    } finally {
      setDistributionReportId(null)
    }
  }

  // For a returned payment with no notice: the reversal was booked with the step skipped, the
  // generation failed at the time, or the NSF came across from a prior servicer.
  const onGenerateNsfNotice = async (item: Record<string, any>) => {
    if (generatingNoticeId) return
    setGeneratingNoticeId(item.id)
    try {
      const res: any = await generateNsfNotice(item.id)
      toast(res?.message || (res?.success ? 'NSF notice generated' : 'Failed to generate the NSF notice'), {
        type: res?.success ? 'success' : 'error',
      })
      // Re-read the row so the view / email actions appear against the notice just created.
      if (res?.success) {
        const rows = await getLoanPayments()
        setData(Array.isArray(rows) ? rows : [])
      }
    } catch {
      toast('Failed to generate the NSF notice', { type: 'error' })
    } finally {
      setGeneratingNoticeId(null)
    }
  }

  // Emailing the notice is deliberately separate from generating it.
  const onSendNsfNotice = async (item: Record<string, any>) => {
    if (sendingNoticeId) return
    const ok = await confirm('Email the NSF notice to the borrower?')
    if (!ok) return
    setSendingNoticeId(item.id)
    try {
      const res: any = await sendNsfNotice(item.id)
      toast(res?.message || (res?.success ? 'NSF notice sent' : 'Failed to send the NSF notice'), {
        type: res?.success ? 'success' : 'error',
      })
    } catch {
      toast('Failed to send the NSF notice', { type: 'error' })
    } finally {
      setSendingNoticeId(null)
    }
  }

  const onReverseReasonChange = (key: string, value: any) => {
    if (key === 'reverseReason') {
      const temp = cloneDeep(reverseReasonInput)
      temp.value = value
      setReverseReasonInput(temp)
      // Reset other reason when changing select
      if (value !== 'Other') {
        const otherTemp = cloneDeep(otherReasonInput)
        otherTemp.value = ''
        setOtherReasonInput(otherTemp)
      }
    } else if (key === 'otherReason') {
      const temp = cloneDeep(otherReasonInput)
      temp.value = value
      setOtherReasonInput(temp)
    }
  }

  const onAddChargeSubmit = async (newData: Record<string, any>) => {
    await addNewLoanCharge({ ...newData, type: LoanChargeType.LoanCharges })
    setShowAddCharge(false)
  }

  const onReverseSubmit = async () => {
    const selectedReason = reverseReasonInput.value as string
    const otherReason = otherReasonInput.value as string
    if (!selectedReason || !reversePayment) return
    if (selectedReason === 'Other' && !otherReason) return

    const reverseReason = selectedReason === 'Other' ? otherReason : selectedReason

    // Every money field a reversal offsets, negated below. The server derives these from the
    // original row too and its answer is what gets stored, so this list only has to agree with
    // REVERSAL_MONEY_FIELDS in the backend's loan-payments entity - a field left out here no
    // longer lands in the database as uncollected income.
    //
    // Interest capitalized into the balance raises the UPB, so reversing has to take it back off;
    // left out, the loan would keep a balance increase the reversal undid. Unpaid and default
    // interest and charges are read raw by the Cash Journal, the reconciliation report and the
    // NetSuite payments feed, so an unsigned one reads there as a second collection.
    //
    // `suspense`, `sourceSuspense` and `sourceImpound` are deliberately absent: each drives a
    // trust-ledger post guarded on a positive amount, so negating one would silently skip the
    // entry that puts the money back. `sourceInterestReserve` is restored by the server from the
    // original row, so its value here never drives anything, and the same goes for
    // `sourceConstructionReserve`, `sourceEscrowRepairHoldback` and `sourceTrustBalance`.
    const numericFields = [
      'amountDue',
      'amountPaid',
      'principal',
      'interest',
      'unpaidInterest',
      'capitalizedInterest',
      'defaultInterest',
      'unpaidDefaultInterest',
      'mortgage',
      'escrows',
      'escrowsTotal',
      'buydown',
      'lateFee',
      'servicingFee',
      'charges',
    ]

    const reversedData: Record<string, any> = {
      notes: reverseReason,
    }

    // Copy all fields from original payment and reverse numeric ones
    Object.keys(reversePayment).forEach((key) => {
      if (numericFields.includes(key)) {
        const value = Number(removeComma(reversePayment[key]) || 0)
        reversedData[key] = value !== 0 ? -value : 0
      } else if (key !== 'id' && key !== 'notes' && key !== 'reversesPaymentId') {
        reversedData[key] = reversePayment[key]
      }
    })

    reversedData.reversed = true
    reversedData.reversesPaymentId = reversePayment.id

    // NSF: book the fee, roll the ACH draft date back, and generate the notice for review.
    const isNsf = selectedReason === NSF_REASON
    if (isNsf) {
      reversedData.nsf = true
      reversedData.nsfFee = Number(removeComma(nsfFee) || 0)
      reversedData.nsfGenerateNotice = nsfGenerateNotice && !!nsfConfig.template
      reversedData.nsfEmailNotice = reversedData.nsfGenerateNotice && nsfEmailNotice
    }

    setIsModalLoading(true)
    let res
    try {
      res = await createLoanPayment(reversedData)
    } catch {
      // The error is already surfaced by the global API error toast (e.g. the loan is on
      // Hold). Reset the modal's loading state and keep it open so the user can adjust and retry.
      setIsModalLoading(false)
      return
    }
    if (res.success) {
      const temp = cloneDeep(data)
      temp.push(res.item)
      setData(temp)
      // A reversal moves the servicing schedule back on the server - Next Payment Date and
      // Interest Paid Thru - so re-read them here too. This submit path is separate from the
      // add/edit one, and was previously the only route that left the dates stale.
      await refreshServicingDates()
      // What the reversal did to the investor payout queue. Worth saying either way: the queue
      // goes quiet whether the payout was pulled back before it went out or clawed back after, and
      // silence there reads as the reversal having done nothing about the money already promised.
      const payouts = (res as any).payouts
      const payoutNotes: string[] = []
      if (payouts?.withdrawnCount)
        payoutNotes.push(`${getPrice2decimal(payouts.withdrawn, false, true)} of queued investor payouts withdrawn`)
      if (payouts?.clawbackCount)
        payoutNotes.push(
          `${getPrice2decimal(payouts.clawback, false, true)} queued as a clawback in the check queue${
            payouts.payees?.length ? ` from ${payouts.payees.join(', ')}` : ''
          }`,
        )
      // The reversal itself is committed even if a fee/notice step failed - say which.
      const nsf = (res as any).nsf
      if (nsf) {
        const done: string[] = []
        const failed: string[] = []
        if (nsf.fee?.chargeId) done.push(`$${nsf.fee.amount} fee added`)
        else if (nsf.fee?.error) failed.push('fee')
        if (nsf.achDate?.to && nsf.achDate.to !== nsf.achDate.from)
          done.push(`ACH date rolled back to ${nsf.achDate.to}`)
        else if (nsf.achDate?.error) failed.push('ACH date')
        if (nsf.notice?.fileName) done.push('notice generated')
        else if (nsf.notice?.error) failed.push('notice')
        if (nsf.email?.success) done.push('notice emailed')
        else if (nsf.email) failed.push(`notice email (${nsf.email.message})`)
        done.push(...payoutNotes)
        toast(`Payment reversed${done.length ? ` - ${done.join(', ')}` : ''}`, { type: 'success' })
        if (failed.length) toast(`NSF step(s) failed: ${failed.join(', ')}. Check the server logs.`, { type: 'error' })
      } else {
        toast(`Successfully reversed loan payment${payoutNotes.length ? `: ${payoutNotes.join(', ')}` : ''}`, {
          type: 'success',
        })
      }
      setShowReverseModal(false)
      setReversePayment(null)
      const resetReasonInput = cloneDeep(reverseReasonInput)
      resetReasonInput.value = ''
      setReverseReasonInput(resetReasonInput)
      const resetOtherReasonInput = cloneDeep(otherReasonInput)
      resetOtherReasonInput.value = ''
      setOtherReasonInput(resetOtherReasonInput)
    } else {
      toast((res as { message?: string }).message || 'Failed to reverse loan payment', { type: 'error' })
    }
    setIsModalLoading(false)
  }

  const onDownload = async (format: 'csv' | 'xls' | 'pdf', isSample = false) => {
    try {
      setIsDownloading(true)
      await downloadLoanPayments(format, isSample)
    } catch (error) {
      toast(isSample ? 'Failed to download sample CSV' : 'Failed to download loan payments', { type: 'error' })
    } finally {
      setIsDownloading(false)
    }
  }

  const creditedValue = useMemo(() => {
    let rtl: Record<string, number> = {
      principal: 0,
      interest: 0,
      defaultInterest: 0,
      escrowsTotal: 0,
      buydown: 0,
      lateFee: 0,
      charges: 0,
      servicingFee: 0,
      otherPayments: 0,
      amountPaid: 0,
      total: 0,
    }
    data.map((item) => {
      rtl.principal += Number(removeComma(item.principal))
      rtl.escrowsTotal += Number(removeComma(item.escrowsTotal))
      rtl.buydown += Number(removeComma(item.buydown))
      rtl.interest += Number(removeComma(item.interest))
      rtl.defaultInterest += Number(removeComma(item.defaultInterest))
      rtl.lateFee += Number(removeComma(item.lateFee))
      rtl.charges += Number(removeComma(item.charges))
      rtl.servicingFee += Number(removeComma(item.servicingFee))
      rtl.otherPayments += ((item.otherPayments || []) as Array<Record<string, any>>).reduce(
        (sum, line) => sum + (Number(line?.amount) || 0),
        0,
      )
      rtl.amountPaid += Number(removeComma(item.amountPaid))
    })
    // Same buckets the grid's Total Credited adds up, so this total ties to Total Amount Paid.
    rtl.total =
      rtl.principal +
      rtl.interest +
      rtl.defaultInterest +
      rtl.escrowsTotal +
      rtl.buydown +
      rtl.lateFee +
      rtl.charges +
      rtl.servicingFee +
      rtl.otherPayments
    Object.keys(rtl).map((key) => {
      rtl[key] = Number(rtl[key].toFixed(2))
    })
    return rtl
  }, [data])

  const escrowDeposit = useMemo(() => {
    let sum = 0
    for (let i = 0; i < 7; i++) {
      const prefix = `PrepaidItems.${i}.`
      if (hud1Info[`${prefix}ReservesPOC`]) continue
      sum += hud1Info[`${prefix}ReservesOV`]
        ? +hud1Info[`${prefix}ReservesOV`]
        : +hud1Info[`${prefix}MonthsInReserve`] * +hud1Info[`${prefix}Payment`]
    }
    sum += calculateAggregateAdjustment(hud1Info)
    return Num(sum.toFixed(2))
  }, [hud1Info])

  const closeImport = () => {
    setShowImport(false)
    setImportFile(null)
  }

  const onDropPaymentHistory = (item: { files?: File[] }) => {
    if (isShared) return
    const file = item?.files?.[0]
    if (!file) return
    if (onHold) {
      toast('Payments are on hold.', { type: 'error' })
      return
    }
    if (!isPaymentHistoryFile(file.name)) {
      toast('Drop a CSV or Excel payment history.', { type: 'error' })
      return
    }
    setImportFile(file)
    setShowImport(true)
  }

  return (
    <TargetBox
      types={[NativeTypes.FILE]}
      canDrop={() => !isShared}
      onDrop={onDropPaymentHistory}
      className="loan-payments-container w-full"
    >
      <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
        <span>Loan Payments</span>
      </h2>
      <LayoutLoading show={isLoading || isDownloading} />
      {onHold && !isShared && <ServicingHoldBanner action="Applying and editing payments" />}
      <p className="text-gray-500">Next Payment Date: {nextPaymentDate ? formatDate(nextPaymentDate) : ''}</p>
      {!isShared && (
        <p className="text-sm text-gray-400">Drop a CSV or Excel payment history here to import it.</p>
      )}
      {!isShared && (
        <div className="flex items-center justify-end gap-4">
          <span
            className={`flex items-center gap-1 ${
              onHold ? 'text-gray-400 cursor-not-allowed' : 'text-shade-blue cursor-pointer hover:underline'
            }`}
            onClick={() => !onHold && onAdd()}
          >
            <PlusCircleIcon className="w-4 h-4" />
            Add
          </span>
          <span
            className="text-shade-blue cursor-pointer flex items-center gap-1 hover:underline"
            onClick={() => onDownload('csv', true)}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Sample CSV
          </span>
          <span
            className="text-shade-blue cursor-pointer flex items-center gap-1 hover:underline"
            onClick={() => {
              setImportFile(null)
              setShowImport(true)
            }}
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
            Import Payments
          </span>
          <span
            className="text-shade-blue cursor-pointer flex items-center gap-1 hover:underline"
            onClick={() => onDownload('csv')}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Download CSV
          </span>
          <span
            className="text-shade-blue cursor-pointer flex items-center gap-1 hover:underline"
            onClick={() => onDownload('xls')}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Download Excel
          </span>
          <span
            className="text-shade-blue cursor-pointer flex items-center gap-1 hover:underline"
            onClick={() => onDownload('pdf')}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Download PDF
          </span>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-4 mt-4 mb-2">
        <InputDateRange
          title="Due Date"
          value={dateFilters.dueDate}
          onChange={(value) => setDateFilters((prev) => ({ ...prev, dueDate: value }))}
        />
        <InputDateRange
          title="Date Received"
          value={dateFilters.receivedDate}
          onChange={(value) => setDateFilters((prev) => ({ ...prev, receivedDate: value }))}
        />
        <InputDateRange
          title="Date Deposited"
          value={dateFilters.depositedDate}
          onChange={(value) => setDateFilters((prev) => ({ ...prev, depositedDate: value }))}
        />
        {Object.values(dateFilters).some((v) => v.from || v.to) && (
          <button
            className="text-xs text-shade-blue hover:underline self-end pb-3"
            onClick={() => setDateFilters({ dueDate: {}, receivedDate: {}, depositedDate: {} })}
          >
            Clear Filters
          </button>
        )}
      </div>
      {/*
        Paging is safe here only because the running Unpaid Balance is walked over the whole
        filtered history upstream in `sortedData`, before the rows are ever sliced. Move that walk
        below the slice and the balance column would only make sense on page one.
      */}
      <PlainTable
        header={tableHeader}
        data={tableData}
        footer={tableFooter}
        isLastColumnStatic
        stickyHeader
        maxHeightClass="max-h-[70vh]"
        hasPagination
        itemCountPerPage={25}
        classNames={columns.map((c) => (c.type === 'number' ? 'text-right' : ''))}
        // A payment history is 20+ columns of one-line values; PlainTable's px-6 py-4 default
        // makes every row nearly three times taller than its content needs. `!` on the header
        // because PlainTable builds its own px-6 py-3 ahead of thClass.
        thClass="!px-2 !py-1.5"
        tdClass="px-2 py-1 whitespace-nowrap"
      />
      {showImport && (
        <PaymentImportModal
          loanNumber={loanNumber}
          initialFile={importFile}
          onClose={closeImport}
          onImported={() => initData()}
        />
      )}
      {!isShared && showAdd && (
        <LoanPaymentsModal
          editData={editData}
          onClose={() => setShowAdd(false)}
          onOk={onSubmit}
          onPhonePaymentApplied={() => {
            // The backend already charged and posted this payment - just close and reload.
            setShowAdd(false)
            initData()
          }}
          payments={data}
          paymentInfo={{ ...paymentInfo, nextPaymentDate }}
          loading={isModalLoading}
        />
      )}
      {showReverseModal && (
        <Modal
          title="Reverse Transaction"
          isOpen={true}
          onClose={() => {
            setShowReverseModal(false)
            setReversePayment(null)
            const resetReasonInput = cloneDeep(reverseReasonInput)
            resetReasonInput.value = ''
            setReverseReasonInput(resetReasonInput)
            const resetOtherReasonInput = cloneDeep(otherReasonInput)
            resetOtherReasonInput.value = ''
            setOtherReasonInput(resetOtherReasonInput)
          }}
          onOk={onReverseSubmit}
          loading={isModalLoading}
          disabled={!reverseReasonInput.value || (reverseReasonInput.value === 'Other' && !otherReasonInput.value)}
        >
          <>
            <div className="p-4">
              <RenderInput input={reverseReasonInput} Key="reverseReason" onChange={onReverseReasonChange} />
              {reverseReasonInput.value === 'Other' && (
                <div className="mt-4">
                  <RenderInput input={otherReasonInput} Key="otherReason" onChange={onReverseReasonChange} />
                </div>
              )}
              {reverseReasonInput.value === NSF_REASON && (
                <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-3">
                  <div className="text-[13px] font-semibold text-orange-800 mb-2">NSF handling</div>
                  <div className="flex items-center gap-2">
                    <label className="text-[13px] text-gray-700 w-24">NSF Fee</label>
                    <input
                      id="nsfFee"
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-32 rounded border border-gray-300 px-2 py-1 text-sm"
                      value={nsfFee}
                      onChange={(e) => setNsfFee(e.target.value)}
                    />
                    <span className="text-[12px] text-gray-500">
                      {nsfConfig.fee ? `default $${nsfConfig.fee}` : 'no default configured'}
                    </span>
                  </div>
                  {nsfConfig.template ? (
                    <>
                      <label
                        htmlFor="nsfGenerateNotice"
                        className="mt-2 flex items-center gap-2 text-[13px] text-gray-700"
                      >
                        <input
                          id="nsfGenerateNotice"
                          type="checkbox"
                          checked={nsfGenerateNotice}
                          onChange={(e) => setNsfGenerateNotice(e.target.checked)}
                        />
                        Generate the NSF notice (attached to the loan for review)
                      </label>
                      {/* Emailing on generation is opt-in; otherwise the notice waits on the reversal row. */}
                      <label
                        htmlFor="nsfEmailNotice"
                        className={`mt-1 ml-5 flex items-center gap-2 text-[13px] ${
                          nsfGenerateNotice ? 'text-gray-700' : 'text-gray-400'
                        }`}
                      >
                        <input
                          id="nsfEmailNotice"
                          type="checkbox"
                          disabled={!nsfGenerateNotice}
                          checked={nsfGenerateNotice && nsfEmailNotice}
                          onChange={(e) => setNsfEmailNotice(e.target.checked)}
                        />
                        Email it to the borrower now
                        {nsfConfig.autoEmail ? '' : ' (otherwise send it later from the reversal row)'}
                      </label>
                    </>
                  ) : (
                    <div className="mt-2 text-[12px] text-gray-500">
                      No NSF notice template configured, so no notice will be generated.
                    </div>
                  )}
                  <div className="mt-2 text-[12px] text-gray-500">
                    The ACH draft date rolls back to the prior due date.
                  </div>
                </div>
              )}
              {reverseReasonInput.value && (
                <div className="flex justify-start mt-4">
                  <span
                    className="text-shade-blue cursor-pointer hover:underline flex items-center gap-1"
                    onClick={() => setShowAddCharge(true)}
                  >
                    <PlusCircleIcon className="w-4 h-4" />
                    Add Charge
                  </span>
                </div>
              )}
            </div>
            {showAddCharge && (
              <AddLoanCharge
                onUpdate={onAddChargeSubmit}
                onClose={() => setShowAddCharge(false)}
                item={null}
                index={-1}
              />
            )}
          </>
        </Modal>
      )}
      <div className="mt-6 flex flex-col gap-3">
        <div>
          <button
            type="button"
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900"
            onClick={() => setShowAmountDue((s) => !s)}
          >
            {showAmountDue ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
            Amount Due
          </button>
          {showAmountDue && (
            <table className="mt-2 w-fit">
              <tbody>
                <tr>
                  {['Title', 'Amount'].map((v) => {
                    return <td className="p-2 border-b border-slate-200 font-semibold text-slate-600">{v}</td>
                  })}
                </tr>
                {paymentInfo?.amountDueDistributTable?.map((item: Array<any>) => {
                  return (
                    <tr>
                      <td className="p-2 border-b border-slate-100">{item[0]}</td>
                      <td className="p-2 border-b border-slate-100 text-right">
                        {getPrice2decimal(item[1], false, true)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
        <div>
          <button
            type="button"
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900"
            onClick={() => setShowBalances((s) => !s)}
          >
            {showBalances ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
            Balances &amp; Credits
          </button>
          {showBalances && (
            <table className="mt-2">
              <tbody>
                <tr>
                  <td></td>
                  {['Amount At Closing', 'Credited', 'Current Amount', 'Purchase Advice', 'Net Amount'].map((v) => {
                    return <td className="p-2 border-b border-slate-200 font-semibold text-slate-600">{v}</td>
                  })}
                </tr>
                {[
                  [
                    'Unpaid Principal Balance',
                    paymentInfo.loanAmount,
                    creditedValue.principal,
                    // Current Amount = servicing Current UPB (fall back to the amortized
                    // calc when Current UPB isn't set), matching the grid's Unpaid Balance.
                    paymentInfo.currentUpb != null && paymentInfo.currentUpb !== ''
                      ? Number(paymentInfo.currentUpb)
                      : +paymentInfo.loanAmount - creditedValue.principal,
                    purchaseAdviceInfo.PAUnpaidPrincipalBalance,
                    '',
                  ],
                  [
                    'Escrow Balance',
                    escrowDeposit,
                    creditedValue.escrowsTotal,
                    escrowDeposit + creditedValue.escrowsTotal,
                    purchaseAdviceInfo.PAEscrowBalance,
                    '',
                  ],
                  [
                    'Buydown Funds',
                    '0.00',
                    creditedValue.buydown,
                    0 - creditedValue.buydown,
                    purchaseAdviceInfo.PABuydownFunds,
                    '',
                  ],
                  ['Interest', '', creditedValue.interest, '', '', ''],
                  ['Default Interest', '', creditedValue.defaultInterest, '', '', ''],
                  ['Late Fees', '', creditedValue.lateFee, '', '', ''],
                  ['Charges', '', creditedValue.charges, '', '', ''],
                  ['Servicing Fees', '', creditedValue.servicingFee, '', '', ''],
                  ['Other Payments', '', creditedValue.otherPayments, '', '', ''],
                ].map((item: Array<any>) => {
                  return (
                    <tr>
                      <td className="p-2 border-b border-slate-100">{item[0]}</td>
                      {item.map((v: any, index: number) => {
                        if (!index) return
                        return (
                          <td className="p-2 border-b border-slate-100 text-right">
                            {convertNegative2Parentheses(getPrice2decimal(v, false, true))}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
                <tr>
                  <td className="p-2">Total Amount Credited</td>
                  <td className="p-2 text-right">{getPrice2decimal(creditedValue.total, false, true)}</td>
                </tr>
                <tr>
                  <td className="p-2">Total Amount Paid</td>
                  <td className="p-2 text-right">{getPrice2decimal(creditedValue.amountPaid, false, true)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </TargetBox>
  )
}
